import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface UseModuleLoadingOptions {
  moduleName: string;
  dependencies?: any[];
  skipInitialLoad?: boolean;
  debounceMs?: number;
  autoRetry?: boolean;
  maxRetries?: number;
}

export function useModuleLoading({
  moduleName,
  dependencies = [],
  skipInitialLoad = false,
  debounceMs = 1000,
  autoRetry = false,
  maxRetries = 3
}: UseModuleLoadingOptions) {
  const { user, moduleLoadingStates, setModuleLoadingState, clearModuleLoadingState } = useAuth();
  const [loading, setLocalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Refs to prevent stale closures and infinite loops
  const hasLoadedRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const lastLoadTimeRef = useRef(0);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if module is already loaded globally
  const isGloballyLoaded = !moduleLoadingStates[moduleName];

  // Memoized loading state setter to prevent unnecessary re-renders
  const setLoadingState = useCallback((isLoading: boolean) => {
    setLocalLoading(isLoading);
    setModuleLoadingState(moduleName, isLoading);
  }, [moduleName, setModuleLoadingState]);

  // Memoized error setter
  const setModuleError = useCallback((errorMessage: string | null) => {
    setError(errorMessage);
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Main loading effect - simplified and optimized
  useEffect(() => {
    // Skip if no user
    if (!user) {
      return;
    }

    // Skip if skip initial load is true and this is the initial mount
    if (skipInitialLoad && isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    // Prevent multiple simultaneous loading states
    if (loading || moduleLoadingStates[moduleName]) {
      return;
    }

    // Prevent rapid successive loads (debounce)
    const now = Date.now();
    if (now - lastLoadTimeRef.current < debounceMs) {
      return;
    }

    // Set loading state
    setLoadingState(true);
    setError(null);
    lastLoadTimeRef.current = now;

    // Mark as loaded after a short delay to prevent flash
    loadingTimeoutRef.current = setTimeout(() => {
      setLoadingState(false);
      hasLoadedRef.current = true;
    }, 150); // Increased to 150ms for better UX

    return cleanup;
  }, [user?.id, moduleName, skipInitialLoad, debounceMs, setLoadingState, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      clearModuleLoadingState(moduleName);
    };
  }, [moduleName, clearModuleLoadingState, cleanup]);

  // Auto-retry logic
  useEffect(() => {
    if (autoRetry && error && retryCount < maxRetries) {
      const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
      
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setError(null);
        setLoadingState(true);
        
        // Auto-clear loading after retry
        setTimeout(() => {
          setLoadingState(false);
        }, 100);
      }, delay);
    }
  }, [error, autoRetry, retryCount, maxRetries, setLoadingState]);

  // Manual retry function
  const retry = useCallback(() => {
    setRetryCount(0);
    setError(null);
    setLoadingState(true);
    
    setTimeout(() => {
      setLoadingState(false);
    }, 100);
  }, [setLoadingState]);

  // Reset function
  const reset = useCallback(() => {
    setRetryCount(0);
    setError(null);
    hasLoadedRef.current = false;
    lastLoadTimeRef.current = 0;
  }, []);

  return {
    loading: loading || moduleLoadingStates[moduleName] || false,
    error,
    retryCount,
    maxRetries,
    setLoading: setLoadingState,
    setError: setModuleError,
    retry,
    reset,
    isGloballyLoaded,
    hasLoaded: hasLoadedRef.current
  };
} 